/**
 * PulseDashboard — dashboard body for non-OEM (Producer) workspaces.
 *
 * Layout mirrors the OEM HomeDashboardPage body exactly (gripp-os
 * PulseHomeMapView uses the same structure — full-canvas map below a white
 * header bar, horizontal card strip pinned to the bottom):
 *  - `pulse-dashboard-topbar` — solid white header: left = TimeRangeSelector,
 *    right = FilterBadge + expandable client-side search.
 *  - `pulse-dashboard-map` (absoluteFill) — ActivityMap. Plots real ASSET
 *    points at rest (resting mode = plotMode null). `searchQuery` filters
 *    the visible markers by name.
 *  - `pulse-dashboard-cardstrip` (absolute bottom overlay) — the PulseCardStrip
 *    (Activity · Assets · Routines · People). Selecting a card lifts its plotMode.
 *
 * All filter/plot state lives in `useHomeDashboardFilters` (shared with the OEM
 * dashboard); this component owns only `range` (the time selector) and renders
 * the chrome. The filter panel opens to half the screen, shrinking the map.
 *
 * This component does NOT self-guard — it is only rendered when the
 * `HomeDashboardPage` has already confirmed the workspace is Producer +
 * PULSE_HOME_DASHBOARD flag is on. See homeDashboardPage.tsx.
 *
 * Web-only — do NOT import from mobile code.
 */
import { SearchIcon, SearchInput, tokens } from '@gripp/shared-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useHomeDashboardFilters } from '../hooks/useHomeDashboardFilters';
import { DEFAULT_TIME_RANGE, TimeRange } from '../types';
import { ActivityMap } from './activityMap';
import { FilterBadge } from './filterBadge';
import { PulseCardStrip } from './pulseCardStrip';
import { PulseFilterPanel } from './pulseFilterPanel';
import { TimeRangeSelector } from './timeRangeSelector';

export const PulseDashboard = () => {
  const { t } = useTranslation();

  const [range, setRange] = useState<TimeRange>(DEFAULT_TIME_RANGE);
  const f = useHomeDashboardFilters();

  return (
    <View style={styles.root} testID="pulse-dashboard">
      {/* Left column — the dashboard (top bar + map + card strip). Shrinks to
          half-width when the filter panel opens beside it. */}
      <View style={styles.leftColumn}>
        {/* Header bar — solid white chrome holding the controls. */}
        <View style={styles.header} testID="pulse-dashboard-topbar">
          <TimeRangeSelector range={range} onChange={setRange} />
          <View style={styles.headerControls}>
            <FilterBadge
              count={f.activeFilterCount}
              onPress={() => f.setFilterOpen(true)}
              label={t('home.filters.label', 'Filters')}
              testID="pulse-dashboard-filter-badge"
              countTestID="pulse-dashboard-filter-count"
            />
            <SearchControl
              value={f.searchQuery}
              onChange={f.setSearchQuery}
              placeholder={t('home.search.placeholder', 'Search')}
            />
          </View>
        </View>

        {/* Map + bottom card overlay fill the area below the header. */}
        <View style={styles.body}>
          <View
            style={styles.mapSlot}
            testID="pulse-dashboard-map"
            accessibilityLabel={t('home.map.label', 'Activity map')}
          >
            <ActivityMap
              range={range}
              plotMode={f.effectivePlotMode}
              statusFilter={f.effectiveStatusFilter}
              searchQuery={f.searchQuery}
              // Filter-panel facets (multi): groups narrow the asset layer;
              // labels + severity buckets + issue types narrow the activity layer
              // (threaded server-side via the home hooks → HomeActivityFilter).
              groupIds={f.groupIds}
              labelIds={f.labelIds}
              severityBuckets={f.severityBuckets}
              issueTypes={f.issueTypes}
              personAccountId={f.personFilter?.id ?? null}
            />
          </View>

          {/* Active-plot indicator (clears the mode + filter) — sits above the
              map. Shows the plotted entity and, when a chart item is selected,
              the sub-filter ("Assets · Needs Repair"). */}
          {(f.effectivePlotMode || f.personFilter) && (
            <View style={styles.plotBannerWrap} pointerEvents="box-none">
              {f.effectivePlotMode && (
                <Pressable
                  style={styles.plotBanner}
                  onPress={f.clearPlot}
                  accessibilityRole="button"
                  accessibilityLabel={t('home.plot.clear', 'Clear map plotting')}
                  testID="pulse-dashboard-plot-clear"
                >
                  <Text style={styles.plotBannerText}>
                    {t(
                      `home.plot.${f.effectivePlotMode.toLowerCase()}`,
                      f.effectivePlotMode
                    )}
                    {/* Activity layer: summarise the multi facets; other layers
                        show their single client-side sub-filter. */}
                    {f.isActivityLayer
                      ? f.activityTypes.length + f.labelIds.length > 0
                        ? ` · ${f.activityTypes.length + f.labelIds.length} ${t(
                            'home.plot.filters',
                            'filters'
                          )}`
                        : ''
                      : f.effectiveStatusFilter
                      ? ` · ${f.effectiveStatusFilter}`
                      : ''}
                  </Text>
                  <Text style={styles.plotBannerClose}>✕</Text>
                </Pressable>
              )}
              {/* Person facet chip — independent of plotMode (People is
                  non-plotting). Shows who the field map is scoped to; clears the
                  facet on press. */}
              {f.personFilter && (
                <Pressable
                  style={styles.plotBanner}
                  onPress={() => f.setPersonFilter(null)}
                  accessibilityRole="button"
                  accessibilityLabel={t(
                    'home.people.clearFilter',
                    'Clear person filter'
                  )}
                  testID="pulse-dashboard-person-clear"
                >
                  <Text style={styles.plotBannerText} numberOfLines={1}>
                    {f.personFilter.name}
                  </Text>
                  <Text style={styles.plotBannerClose}>✕</Text>
                </Pressable>
              )}
            </View>
          )}

          {/*
           * Card strip — the Pulse cards (Activity · Assets · Routines · People).
           * Selecting a card lifts its plotMode (replotting the map); clicking a
           * chart item lifts the sub-filter. Overlaid on the bottom of the map.
           */}
          <View style={styles.cardStripWrap} pointerEvents="box-none">
            <PulseCardStrip
              range={range}
              plotMode={f.effectivePlotMode}
              statusFilter={f.cardStatusFilter}
              onSelectPlot={f.handleSelectPlot}
              onSelectStatus={f.handleSelectStatus}
              selectedPersonId={f.personFilter?.id ?? null}
              onSelectPerson={f.handleSelectPerson}
            />
          </View>
        </View>
      </View>

      {/* Right column — the filter panel opens to half the screen, shrinking the
          map (left column) to the other half. Carries its own header (back +
          Filter (N) + reset); toggled by the FilterBadge. */}
      {f.filterOpen && (
        <View style={styles.rightColumn}>
          <PulseFilterPanel
            categories={f.filterCategories}
            selections={f.filterSelections}
            activeCount={f.activeFilterCount}
            onSelect={f.handleSelectFilter}
            onReset={f.clearFilters}
            onClose={() => f.setFilterOpen(false)}
          />
        </View>
      )}
    </View>
  );
};

/**
 * SearchControl — the header's collapsed search button that expands to the
 * Crop Circle `SearchInput` (design: gripp-os PulseHomeMapView collapsed
 * search, identical to OEM HomeDashboardPage). SearchInput owns the search
 * icon, clear button, and web outline suppression; this only manages the
 * collapsed↔expanded toggle.
 */
const SearchControl = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);

  if (!open && !value) {
    return (
      <Pressable
        style={[styles.searchButton, hovered && styles.searchButtonHovered]}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={placeholder}
        testID="pulse-dashboard-search"
      >
        <SearchIcon size={20} color={tokens.colors.neutral[800]} />
      </Pressable>
    );
  }

  return (
    <SearchInput
      value={value}
      onChangeText={onChange}
      placeholder={placeholder}
      autoFocus
      height={36}
      width={220}
      onBlur={() => {
        if (!value) setOpen(false);
      }}
      testID="pulse-dashboard-search-field"
    />
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: tokens.colors.brand.neutral,
    overflow: 'hidden',
    // The page is mounted full-bleed (Layout drops the shared content gutter for
    // /home so the OEM map sits edge-to-edge). Pulse wants a small left gutter so
    // the map/header don't butt against the left nav — matches the CC page gutter.
    paddingLeft: tokens.spacing.lg, // 16
  },
  // Dashboard column — full width on its own, shrinks to share the row with the
  // filter panel (rightColumn) when it opens.
  leftColumn: {
    flex: 1,
    overflow: 'hidden',
  },
  // Filter panel column — half the screen, so the map (leftColumn) shrinks to
  // the other half rather than being covered.
  rightColumn: {
    width: '50%',
  },
  // Solid white header chrome (design: --card on a --border-section divider,
  // matching PulseHomeMapView's header — same tokens as OEM HomeDashboardPage).
  header: {
    height: tokens.spacing['7xl'], // 56px
    paddingHorizontal: tokens.spacing.lg,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: tokens.colors.brand.white,
    borderBottomWidth: 1,
    borderBottomColor: tokens.colors.slate[100],
    zIndex: 10,
  },
  body: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
  },
  mapSlot: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: tokens.colors.brand.neutral,
  },
  // Right-side header controls (filter badge + search), grouped so they sit
  // together at the end of the header row.
  headerControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  searchButton: {
    width: 36,
    height: 36,
    borderRadius: tokens.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchButtonHovered: {
    backgroundColor: tokens.colors.neutral[200],
  },
  plotBannerWrap: {
    position: 'absolute',
    top: tokens.spacing.md,
    left: tokens.spacing.lg,
    zIndex: 10,
    gap: tokens.spacing.xs,
    alignItems: 'flex-start',
  },
  plotBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    paddingHorizontal: tokens.spacing.md,
    borderRadius: tokens.borderRadius.full,
    backgroundColor: tokens.colors.neutral[800],
  },
  plotBannerText: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.brand.white,
  },
  plotBannerClose: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[400],
  },
  cardStripWrap: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
});
